import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, Trash2, Edit2, X, Clock, Copy } from 'lucide-react';
import api from '../../api';

const STAFF_COLORS = ['#5de6c8','#4a9eff','#ff6b6b','#ffd93d','#a855f7','#f97316','#10b981','#ec4899','#6366f1','#14b8a6','#f59e0b','#ef4444'];
const DAY_NAMES = { monday:'Lunes', tuesday:'Martes', wednesday:'Miércoles', thursday:'Jueves', friday:'Viernes', saturday:'Sábado', sunday:'Domingo' };
const DAY_KEYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

export default function AppointmentConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEmployee = user?.user_type === 'employee';
  const isOwner = user?.user_type === 'owner';
  const businessId = isEmployee ? user?.parent_user_id : user?.id;
  const [tab, setTab] = useState('services');
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [hours, setHours] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Service modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [svcName, setSvcName] = useState('');
  const [svcCost, setSvcCost] = useState('');
  const [svcDuration, setSvcDuration] = useState('');

  // Staff modal
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [stName, setStName] = useState('');
  const [stColor, setStColor] = useState(STAFF_COLORS[0]);
  const [stLinkedUser, setStLinkedUser] = useState('');

  const loadAll = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [svcR, stR, hrR] = await Promise.all([
        api.get(`/appointment-services/${businessId}`),
        api.get(`/appointment-staff/${businessId}`),
        api.get(`/business-hours/${businessId}`)
      ]);
      setServices(svcR.data || []);
      setStaffList(stR.data || []);
      setHours(hrR.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load employees for linking (include the owner themselves so they can register as staff/barber)
  useEffect(() => {
    if (!businessId || !user?.id) return;
    const ownerId = user.user_type === 'owner' ? user.id : user.parent_user_id;
    if (!ownerId) return;
    const ownerEntry = isOwner
      ? [{ id: user.id, employee_name: `${user.owner_name || 'Dueño'} (yo)`, username: user.username }]
      : [];
    api.get(`/business/owner/${ownerId}/staff`)
      .then(r => setEmployees([...ownerEntry, ...(r.data || [])]))
      .catch(() => setEmployees(ownerEntry));
  }, [user, businessId, isOwner]);

  // Services
  const openCreateService = () => { setEditingService(null); setSvcName(''); setSvcCost(''); setSvcDuration(''); setShowServiceModal(true); };
  const openEditService = (s) => { setEditingService(s); setSvcName(s.name); setSvcCost(String(s.cost)); setSvcDuration(String(s.duration_minutes)); setShowServiceModal(true); };
  const saveService = async () => {
    if (!svcName || !svcCost || !svcDuration) { alert('Completa todos los campos'); return; }
    const body = { business_user_id: businessId, name: svcName, cost: parseFloat(svcCost), duration_minutes: parseInt(svcDuration) };
    try {
      if (editingService) await api.put(`/appointment-services/${editingService.id}`, body);
      else await api.post('/appointment-services', body);
      setShowServiceModal(false);
      loadAll();
    } catch (e) { alert('Error'); }
  };
  const deleteService = async (id) => {
    if (!window.confirm('¿Eliminar servicio?')) return;
    await api.delete(`/appointment-services/${id}`);
    loadAll();
  };

  // Staff
  const openCreateStaff = () => { setEditingStaff(null); setStName(''); setStColor(STAFF_COLORS[0]); setStLinkedUser(''); setShowStaffModal(true); };
  const openEditStaff = (s) => { setEditingStaff(s); setStName(s.staff_name); setStColor(s.color); setStLinkedUser(s.linked_user_id || ''); setShowStaffModal(true); };
  const saveStaff = async () => {
    if (!stName || !stColor) { alert('Completa todos los campos'); return; }
    const body = { business_user_id: businessId, staff_name: stName, color: stColor, linked_user_id: stLinkedUser || null };
    try {
      if (editingStaff) await api.put(`/appointment-staff/${editingStaff.id}`, body);
      else await api.post('/appointment-staff', body);
      setShowStaffModal(false);
      loadAll();
    } catch (e) { alert('Error'); }
  };
  const deleteStaff = async (id) => {
    if (!window.confirm('¿Eliminar personal?')) return;
    await api.delete(`/appointment-staff/${id}`);
    loadAll();
  };

  // Hours
  const updateDayHours = async (day, field, value) => {
    const updated = { ...hours, [day]: { ...hours[day], [field]: value } };
    setHours(updated);
    try {
      await api.put(`/business-hours/${businessId}`, { [day]: updated[day] });
    } catch (e) { console.error(e); }
  };

  const setDayHours = async (day, patch) => {
    const updated = { ...hours, [day]: { ...hours[day], ...patch } };
    setHours(updated);
    try {
      await api.put(`/business-hours/${businessId}`, { [day]: updated[day] });
    } catch (e) { console.error(e); }
  };

  // Fetch slug if missing from cached session (existing users logged in before slug was introduced)
  const [fetchedSlug, setFetchedSlug] = useState('');
  useEffect(() => {
    if (!user?.id || user?.slug) return;
    const targetId = isEmployee ? user?.parent_user_id : user?.id;
    if (!targetId) return;
    api.get(`/business/users/${targetId}`)
      .then(r => { if (r.data?.slug) setFetchedSlug(r.data.slug); })
      .catch(() => {});
  }, [user, isEmployee]);

  const bookingSlug = user?.slug || fetchedSlug || user?.username || '';
  const bookingLink = `${window.location.origin}/reservar/${bookingSlug}`;
  const copyLink = () => { navigator.clipboard.writeText(bookingLink); alert('¡Link copiado!'); };

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Configuración de Turnos</h1>
      </div>

      {/* Booking Link */}
      <div style={{ padding: '0 16px 12px' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>Link para clientes</div>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, wordBreak: 'break-all' }}>/reservar/{bookingSlug}</div>
          </div>
          <button className="icon-btn accent-text" onClick={copyLink}><Copy size={18} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="period-tabs" style={{ paddingTop: 0, paddingBottom: 0 }}>
        {[['services','Servicios'], ...(isOwner ? [['staff','Personal']] : []), ['hours','Horarios']].map(([k,l]) => (
          <button key={k} className={`period-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      <div className="content-scroll">
        {loading ? <div className="spinner" style={{ marginTop: 40 }} /> : (
          <>
            {/* SERVICES TAB */}
            {tab === 'services' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontWeight: 600 }}>Servicios de Turno</h3>
                  <button className="btn-primary" style={{ padding: '8px 14px', fontSize: '0.85rem' }} onClick={openCreateService}>
                    <Plus size={14} style={{ marginRight: 4 }} />Agregar
                  </button>
                </div>
                {services.length === 0 ? (
                  <div className="empty-state"><p>No hay servicios. Agregá el primero.</p></div>
                ) : services.map(s => (
                  <div key={s.id} className="card" style={{ marginBottom: 10 }} data-testid={`service-card-${s.id}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{s.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text2)', display: 'flex', gap: 12, marginTop: 2 }}>
                          <span>${s.cost.toLocaleString('es-AR')}</span>
                          <span><Clock size={12} style={{ verticalAlign: 'middle' }} /> {s.duration_minutes} min</span>
                        </div>
                      </div>
                      <button className="icon-btn" onClick={() => openEditService(s)}><Edit2 size={16} /></button>
                      <button className="icon-btn" style={{ color: 'var(--danger)' }} onClick={() => deleteService(s.id)}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* STAFF TAB */}
            {tab === 'staff' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontWeight: 600 }}>Personal</h3>
                  <button className="btn-primary" style={{ padding: '8px 14px', fontSize: '0.85rem' }} onClick={openCreateStaff}>
                    <Plus size={14} style={{ marginRight: 4 }} />Agregar
                  </button>
                </div>
                {staffList.length === 0 ? (
                  <div className="empty-state"><p>Agregá los nombres del equipo para que los clientes puedan elegir.</p></div>
                ) : staffList.map(s => (
                  <div key={s.id} className="card" style={{ marginBottom: 10 }} data-testid={`staff-config-card-${s.id}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: s.color }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{s.staff_name}</div>
                        {s.linked_user_id && <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>Vinculado a empleado</div>}
                      </div>
                      <button className="icon-btn" onClick={() => openEditStaff(s)}><Edit2 size={16} /></button>
                      <button className="icon-btn" style={{ color: 'var(--danger)' }} onClick={() => deleteStaff(s.id)}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* HOURS TAB */}
            {tab === 'hours' && (
              <>
                <h3 style={{ fontWeight: 600, marginBottom: 12 }}>Horarios de Atención</h3>
                {DAY_KEYS.map(day => {
                  const d = hours[day] || { is_open: false, start_time: '09:00', end_time: '19:00', split: false };
                  return (
                    <div key={day} className="card" style={{ marginBottom: 10 }} data-testid={`hours-${day}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                          <div style={{
                            width: 40, height: 22, borderRadius: 11,
                            background: d.is_open ? 'var(--accent)' : 'var(--border)',
                            position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0
                          }} onClick={() => updateDayHours(day, 'is_open', !d.is_open)}>
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%', background: '#fff',
                              position: 'absolute', top: 2, left: d.is_open ? 20 : 2, transition: 'left 0.2s'
                            }} />
                          </div>
                          <span style={{ fontWeight: 600 }}>{DAY_NAMES[day]}</span>
                        </label>
                        {!d.is_open && <span style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>Cerrado</span>}
                      </div>

                      {d.is_open && (
                        <>
                          {/* Selector Corrido / Cortado */}
                          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                            <button type="button" className={`freq-btn ${!d.split ? 'active' : ''}`}
                              style={{ flex: 1, fontSize: '0.78rem', padding: '6px 8px' }}
                              data-testid={`hours-${day}-corrido`}
                              onClick={() => updateDayHours(day, 'split', false)}>Horario de corrido</button>
                            <button type="button" className={`freq-btn ${d.split ? 'active' : ''}`}
                              style={{ flex: 1, fontSize: '0.78rem', padding: '6px 8px' }}
                              data-testid={`hours-${day}-cortado`}
                              onClick={() => setDayHours(day, {
                                split: true,
                                start_time_2: d.start_time_2 || '17:00',
                                end_time_2: d.end_time_2 || '22:00',
                              })}>Horario cortado</button>
                          </div>

                          {/* Período 1 */}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                            {d.split && <span style={{ fontSize: '0.75rem', color: 'var(--text2)', width: 24 }}>1°</span>}
                            <input type="time" className="input" style={{ flex: 1, padding: '6px 8px' }}
                              value={d.start_time} onChange={e => updateDayHours(day, 'start_time', e.target.value)} />
                            <span style={{ color: 'var(--text2)' }}>-</span>
                            <input type="time" className="input" style={{ flex: 1, padding: '6px 8px' }}
                              value={d.end_time} onChange={e => updateDayHours(day, 'end_time', e.target.value)} />
                          </div>

                          {/* Período 2 (solo en horario cortado) */}
                          {d.split && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text2)', width: 24 }}>2°</span>
                              <input type="time" className="input" style={{ flex: 1, padding: '6px 8px' }}
                                data-testid={`hours-${day}-start2`}
                                value={d.start_time_2 || '17:00'} onChange={e => updateDayHours(day, 'start_time_2', e.target.value)} />
                              <span style={{ color: 'var(--text2)' }}>-</span>
                              <input type="time" className="input" style={{ flex: 1, padding: '6px 8px' }}
                                data-testid={`hours-${day}-end2`}
                                value={d.end_time_2 || '22:00'} onChange={e => updateDayHours(day, 'end_time_2', e.target.value)} />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ flex: 1, margin: 0 }}>{editingService ? 'Editar Servicio' : 'Nuevo Servicio'}</h3>
              <button className="icon-btn" onClick={() => setShowServiceModal(false)}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label className="label">Nombre del Servicio</label>
              <input className="input" data-testid="service-name-input" placeholder="Ej: Corte de cabello" value={svcName} onChange={e => setSvcName(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Costo ($)</label>
                <input className="input" data-testid="service-cost-input" type="number" placeholder="0" value={svcCost} onChange={e => setSvcCost(e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Duración (min)</label>
                <input className="input" data-testid="service-duration-input" type="number" placeholder="30" value={svcDuration} onChange={e => setSvcDuration(e.target.value)} />
              </div>
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowServiceModal(false)}>Cancelar</button>
              <button className="btn-primary" data-testid="save-service-btn" onClick={saveService}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Modal */}
      {showStaffModal && (
        <div className="modal-overlay" onClick={() => setShowStaffModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ flex: 1, margin: 0 }}>{editingStaff ? 'Editar Personal' : 'Agregar Personal'}</h3>
              <button className="icon-btn" onClick={() => setShowStaffModal(false)}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label className="label">Nombre</label>
              <input className="input" data-testid="staff-config-name-input" placeholder="Ej: Carlos" value={stName} onChange={e => setStName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Color en Agenda</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {STAFF_COLORS.map(c => (
                  <div key={c} onClick={() => setStColor(c)} style={{
                    width: 32, height: 32, borderRadius: '50%', background: c, cursor: 'pointer',
                    border: stColor === c ? '3px solid var(--text)' : '3px solid transparent',
                    boxSizing: 'border-box'
                  }} />
                ))}
              </div>
            </div>
            {employees.length > 0 && (
              <div className="form-group">
                <label className="label">Vincular a empleado (opcional)</label>
                <select className="input" value={stLinkedUser} onChange={e => setStLinkedUser(e.target.value)}>
                  <option value="">Sin vincular</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.employee_name} (@{e.username})</option>)}
                </select>
              </div>
            )}
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowStaffModal(false)}>Cancelar</button>
              <button className="btn-primary" data-testid="save-staff-config-btn" onClick={saveStaff}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
